import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";
import {
  LOGGED_IN_BOOTSTRAP_CAPABILITIES,
  QrGenerator,
  buildSaveInput,
  type QrGeneratorState,
  type SaveInputValue,
} from "@qrody/qr-generator";
import { api, ApiError } from "@/lib/api";
import { useTranslations } from "@/i18n/IntlProvider";

/**
 * /$locale/codes/new — authenticated QR-code editor.
 *
 * Mounts the shared `<QrGenerator>` from `@qrody/qr-generator` and
 * supplies a Vite/BFF-flavoured save action via the `actionSlot` prop.
 * The generator owns the form + preview lifecycle (slider above QR,
 * `min-w-[min(16rem,100%)]` clamp, mobile order Preview→Content→Design
 * — all carried over from the 2026-05-11 polish work).
 *
 * Save flow
 * ---------
 * The generator's `buildSaveInput` mapper translates the live form
 * state into a `(payload, design, options)` triple. We push that to
 * `POST /v1/codes` through the same-origin `api()` wrapper — the BFF
 * session cookie + X-XSRF-TOKEN double-submit token attach
 * automatically. On 201 we navigate to the freshly minted code's
 * detail page. 422 (validation) and 402 (quota / payment) surface
 * inline as a small banner above the inline-action row.
 *
 * Why the generator stays where it is: the package is surface-
 * agnostic (marketing/ uses the same `<QrGenerator>` directly with
 * anonymous capabilities). Copying 4 000 LOC of editor into the
 * Vite SPA would (a) blow the sub-iter #1 LOC budget and (b) fork
 * the brand-defining feature into two copies that immediately drift.
 * The Next-runtime hooks the package reaches for (`next-intl`,
 * `next/navigation`, `next/link`) are aliased to local shims in
 * `vite.config.ts` + `tsconfig.app.json`.
 */
export function CodesNewRoute() {
  const t = useTranslations();
  const navigate = useNavigate();
  const { locale } = useParams({ strict: false }) as { locale?: string };
  const lang = locale ?? "de";

  return (
    <section className="mx-auto max-w-screen-2xl">
      <header className="mx-auto max-w-3xl text-center">
        <h1 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
          {t("app.codes.new.title")}
        </h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          {t("app.codes.new.subtitle")}
        </p>
      </header>

      <div className="mt-4 sm:mt-6">
        <QrGenerator
          caps={LOGGED_IN_BOOTSTRAP_CAPABILITIES}
          actionSlot={(state) => (
            <SaveButton
              state={state}
              onSaved={(id) => {
                void navigate({
                  to: "/$locale/codes/$id" as never,
                  params: { locale: lang, id } as never,
                });
              }}
            />
          )}
        />
      </div>

      <div className="mt-6 text-center">
        <Link
          to={"/$locale/codes" as never}
          params={{ locale: lang } as never}
          className="inline-flex h-10 items-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          {"← "}
          {t("app.codes.new.back")}
        </Link>
      </div>
    </section>
  );
}

/**
 * Caller-supplied action button rendered by `<QrGenerator>` in place of
 * the marketing download button (the `primaryAction: "save"` cap flips
 * the generator's action bar over to this slot). Stays small on purpose
 * — the heavy lifting (form, preview, validation) is the generator's.
 */
function SaveButton({
  state,
  onSaved,
}: {
  state: QrGeneratorState;
  onSaved: (id: string) => void;
}): ReactNode {
  const t = useTranslations();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveInput = useSaveInput(state);
  // The QR matrix can be live even when the underlying payload fails
  // schema validation (URL with no scheme, vCard with empty fullName).
  // The generator surfaces a friendly message in that case; we just
  // disable save so we never POST a code the scanner won't read.
  const disabled = !state.validationOk || saveInput === null;

  const mutation = useMutation<{ id: string }, ApiError>({
    mutationFn: async () => {
      if (!saveInput) throw new Error("buildSaveInput returned null");
      const body = toCreateQrCodeRequest(saveInput);
      return api<{ id: string }>("/v1/codes", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onMutate: () => setErrorMessage(null),
    onSuccess: ({ id }) => onSaved(id),
    onError: (error) => setErrorMessage(translateSaveError(error, t)),
  });

  const busy = mutation.isPending;

  return (
    <div className="flex w-full flex-col gap-2">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={disabled || busy}
        aria-label={t("app.codes.new.cta")}
        className="inline-flex items-center justify-center gap-1.5 self-start rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Save className="h-4 w-4" aria-hidden />
        {busy ? t("app.codes.new.inFlight") : t("app.codes.new.cta")}
      </button>
      {errorMessage ? (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Drive `buildSaveInput` from the live editor state. Returns `null`
 * when the form has no payload yet (e.g. URL field empty on first
 * mount) or when the dynamic/static toggle conflicts with the payload
 * variant (Wi-Fi cannot be dynamic — only URL / file can).
 *
 * `state.mode` must be forwarded as `forceMode` — otherwise
 * `buildSaveInput` falls back to "DYNAMIC for any dynamic-compatible
 * payload", which silently overrides the user's Static pick and
 * surfaces as a confusing `qr.dynamic.max_active` 402 on Free accounts
 * that already hold their 1 allowed dynamic code.
 */
function useSaveInput(state: QrGeneratorState): SaveInputValue | null {
  if (state.payload == null) return null;
  const result = buildSaveInput({
    payload: state.payload,
    design: state.design,
    forceMode: state.mode,
  });
  return result.ok ? result.value : null;
}

/**
 * Translate `buildSaveInput`'s typed payload + design into the
 * wire-shape `CreateQrCodeRequest` defined in
 * `modules/qrcode/.../api/QrCodeDtos.kt`. Exactly one payload variant
 * must be present (`PayloadDto.toDomain()` enforces that on the
 * Spring side); we let the discriminator on `CreateCodePayload.kind`
 * pick the matching field.
 */
function toCreateQrCodeRequest(input: SaveInputValue): Record<string, unknown> {
  const payload = input.payload;
  const payloadBlock: Record<string, unknown> = {};
  switch (payload.kind) {
    case "url":
      payloadBlock.url = { url: payload.url };
      break;
    case "text":
      payloadBlock.text = { text: payload.text };
      break;
    case "email":
      payloadBlock.email = stripEmpty({
        to: payload.to,
        subject: payload.subject,
        body: payload.body,
      });
      break;
    case "sms":
      payloadBlock.sms = stripEmpty({ to: payload.to, message: payload.message });
      break;
    case "geo":
      payloadBlock.geo = { latitude: payload.latitude, longitude: payload.longitude };
      break;
    case "wifi":
      payloadBlock.wifi = stripEmpty({
        ssid: payload.ssid,
        password: payload.password,
        authentication: payload.authentication,
        hidden: payload.hidden,
      });
      break;
    case "vcard":
      payloadBlock.vcard = stripEmpty({
        fullName: payload.fullName,
        email: payload.email,
        phone: payload.phone,
        org: payload.org,
        title: payload.title,
        url: payload.url,
      });
      break;
    case "file":
      payloadBlock.file = { downloadUrl: payload.downloadUrl };
      break;
  }

  const designBlock: Record<string, unknown> = {
    foreground: input.design.foreground,
    background: input.design.background,
    logoAssetId: input.design.logoAssetId ?? null,
    frame: input.design.frame ?? "NONE",
  };
  if (input.design.ecc) designBlock.ecc = input.design.ecc;
  if (input.design.minVersion != null) designBlock.minVersion = input.design.minVersion;

  const body: Record<string, unknown> = {
    payload: payloadBlock,
    design: designBlock,
  };
  if (input.options.mode) body.mode = input.options.mode;
  return body;
}

function stripEmpty<T extends Record<string, unknown>>(value: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    if (val !== undefined && val !== null && val !== "") out[key] = val;
  }
  return out;
}

/**
 * Bucket the BFF's problem-detail response into a short, user-facing
 * sentence. The two interesting branches for /codes/new:
 *   - 422 — validation (Spring's MethodArgumentNotValidException);
 *           the body's `detail` field reads "must start with http://"
 *           or similar.
 *   - 402 — quota / payment required (RequiresFeature + Plan-Matrix);
 *           the body's `user_action` field points the user at /billing.
 * Anything else falls through to the verbatim message — the inline
 * banner is short on purpose; the route-level guard surfaces a full
 * <ErrorBanner> in follow-up sub-iterations if we need rich
 * help-link UX.
 */
function translateSaveError(error: ApiError, t: ReturnType<typeof useTranslations>): string {
  const body = (error.body ?? {}) as {
    detail?: string;
    title?: string;
    user_action?: string;
  };
  const detail = body.user_action ?? body.detail ?? body.title;
  const prefix =
    error.status === 422
      ? t("app.codes.new.error.validation")
      : error.status === 402
        ? t("app.codes.new.error.quota")
        : t("app.codes.new.error.generic");
  return detail ? `${prefix} ${detail}` : prefix;
}
